import { useCallback, useEffect, useState } from "react";
import { CheckCircle2, Eye, Mail, Send, ShieldAlert, ShieldCheck, Unlock, X } from "lucide-react";
import { EditorModal } from "@/components/EditorModal";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";

const EmailPreview = ({ preview, onClose, testId }) => (
  <EditorModal onClose={onClose} testId={testId}>
    <section className="inline-edit-panel email-preview-panel" data-testid={`${testId}-panel`}>
      <div className="section-heading">
        <div>
          <h2><Mail size={16} /> Email preview</h2>
          <p data-testid={`${testId}-meta`}>To: {preview.to || preview.recipients?.join(", ") || "—"} · Subject: {preview.subject}</p>
        </div>
        <button aria-label="Close preview" className="projection-icon-button" data-testid={`${testId}-close`} type="button" onClick={onClose}><X size={16} /></button>
      </div>
      <iframe className="email-preview-frame" data-testid={`${testId}-frame`} sandbox="" srcDoc={`<body style="margin:0;padding:12px;background:#F4F7F6;font-family:Arial,sans-serif">${preview.html}</body>`} title="Email preview" />
    </section>
  </EditorModal>
);

const GateCard = ({ gate, assignmentId, isAdmin, onChanged }) => {
  const [note, setNote] = useState("");
  const [error, setError] = useState("");
  async function toggle(item) {
    try {
      onChanged((await api.post(`/assignments/${assignmentId}/pre-start/manual`, { item_id: item.key, done: item.blocking })).data);
    } catch (e) { setError(formatApiError(e)); }
  }
  async function override(event) {
    event.preventDefault();
    try {
      onChanged((await api.post(`/assignments/${assignmentId}/pre-start/override`, { note })).data);
      setNote("");
    } catch (e) { setError(formatApiError(e)); }
  }
  async function removeOverride() {
    try { onChanged((await api.delete(`/assignments/${assignmentId}/pre-start/override`)).data); } catch (e) { setError(formatApiError(e)); }
  }
  const Icon = gate.status === "cleared" ? ShieldCheck : gate.status === "overridden" ? Unlock : ShieldAlert;
  return (
    <div className={`prestart-gate is-${gate.status}`} data-testid="prestart-gate">
      <div className="prestart-gate-head">
        <Icon size={16} />
        <strong data-testid="prestart-gate-status">{gate.status === "cleared" ? "Pre-start clearances complete" : gate.status === "overridden" ? "Pre-start gate overridden" : `Start blocked · ${gate.blocking.length} clearance${gate.blocking.length === 1 ? "" : "s"} outstanding`}</strong>
      </div>
      {error && <p className="form-message error-message" data-testid="prestart-gate-error">{error}</p>}
      {gate.items.length > 0 && (
        <ul className="prestart-items" data-testid="prestart-items">
          {gate.items.map((item) => (
            <li key={item.key} className={item.blocking ? "is-blocking" : "is-clear"} data-testid={`prestart-item-${item.key}`}>
              <span>{item.label}{item.manual ? " · site item" : ""}</span>
              <em>{item.status}</em>
              {item.manual && <button className="link-button" data-testid={`prestart-manual-toggle-${item.key}`} type="button" onClick={() => toggle(item)}>{item.blocking ? "Mark done" : "Reopen"}</button>}
            </li>
          ))}
        </ul>
      )}
      {gate.items.length === 0 && <p className="pairing-meta">No clearance requirements apply to this client and role.</p>}
      {gate.override && <p className="pairing-meta" data-testid="prestart-override-note">Overridden by {gate.override.by_name} on {new Date(gate.override.at).toLocaleString()}: “{gate.override.note}”{isAdmin && <> · <button className="link-button" data-testid="prestart-override-remove" type="button" onClick={removeOverride}>remove override</button></>}</p>}
      {gate.status === "blocked" && isAdmin && (
        <form className="prestart-override-form" data-testid="prestart-override-form" onSubmit={override}>
          <input data-testid="prestart-override-input" minLength={5} placeholder="Reason to allow the start anyway (logged)" required value={note} onChange={(e) => setNote(e.target.value)} />
          <button className="button button-secondary" data-testid="prestart-override-submit" type="submit"><Unlock size={14} /> Override</button>
        </form>
      )}
    </div>
  );
};

const IntroductionCard = ({ assignmentId, onSent }) => {
  const [context, setContext] = useState(null);
  const [chosen, setChosen] = useState([]);
  const [extra, setExtra] = useState("");
  const [note, setNote] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    api.get(`/assignments/${assignmentId}/introduction`).then((response) => { setContext(response.data); setChosen(response.data.suggested_recipients.slice(0, 1).map((r) => r.email)); }).catch((e) => setError(formatApiError(e)));
  }, [assignmentId]);
  if (error && !context) return <p className="form-message error-message" data-testid="introduction-error">{error}</p>;
  if (!context) return null;
  const recipients = [...chosen, ...extra.split(/[,\s]+/).filter(Boolean)];
  async function run(kind) {
    setBusy(true); setError("");
    try {
      const response = await api.post(`/assignments/${assignmentId}/introduction/${kind}`, { recipients, note });
      if (kind === "preview") setPreview({ ...response.data, recipients });
      else { onSent(response.data); setContext({ ...context, introduction: response.data }); }
    } catch (e) { setError(formatApiError(e)); } finally { setBusy(false); }
  }
  return (
    <div className="introduction-card" data-testid="introduction-card">
      <h4>“Meet your new team member” for the school</h4>
      <p className="pairing-meta">Name, role, schedule, start date and verified credentials only — no rates or personal contact details. Choose who at {context.client_name} receives it.</p>
      {error && <p className="form-message error-message" data-testid="introduction-error">{error}</p>}
      <div className="introduction-recipients" data-testid="introduction-recipients">
        {context.suggested_recipients.map((r) => (
          <label key={r.email}><input checked={chosen.includes(r.email)} data-testid={`introduction-recipient-${r.email}`} type="checkbox" onChange={(e) => setChosen(e.target.checked ? [...chosen, r.email] : chosen.filter((x) => x !== r.email))} /> {r.label} <small>{r.email}</small></label>
        ))}
        <input data-testid="introduction-extra-recipients" placeholder="Other school emails, comma separated" value={extra} onChange={(e) => setExtra(e.target.value)} />
      </div>
      <textarea data-testid="introduction-note" placeholder="Optional note to the school (e.g. who to meet on day one)" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
      <div className="button-row">
        <button className="button button-secondary" data-testid="introduction-preview" disabled={busy} type="button" onClick={() => run("preview")}><Eye size={14} /> Preview</button>
        <button className="button button-primary" data-testid="introduction-send" disabled={busy || !recipients.length} type="button" onClick={() => run("send")}><Send size={14} /> {context.introduction ? "Send again" : "Send introduction"}</button>
        {context.introduction && <small className="pairing-meta" data-testid="introduction-state">Sent {new Date(context.introduction.sent_at).toLocaleString()} to {context.introduction.recipients.map((r) => r.email).join(", ")}{context.introduction.recipients.some((r) => !r.sent) ? ` · ${context.introduction.recipients.find((r) => !r.sent).error}` : ""}</small>}
      </div>
      {preview && <EmailPreview preview={preview} testId="introduction-email-preview" onClose={() => setPreview(null)} />}
    </div>
  );
};

export const AssignmentPacketPanel = ({ assignmentId, role, onClose, onSent, compact = false }) => {
  const { user } = useAuth();
  const [context, setContext] = useState(null);
  const [form, setForm] = useState({ schedule_override: "", caseload_initials: "", supervisor_name: "", notes: "", template_id: "" });
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const isAdmin = (role || user?.role) === "system_administrator";

  const load = useCallback(() => api.get(`/assignments/${assignmentId}/packet`).then((response) => { setContext(response.data); setForm((current) => ({ ...current, ...response.data.prefill })); }).catch((e) => setError(formatApiError(e))), [assignmentId]);
  useEffect(() => { load(); }, [load]);

  async function run(kind) {
    setBusy(true); setError(""); setMessage("");
    try {
      const response = await api.post(`/assignments/${assignmentId}/packet/${kind}`, form);
      if (kind === "preview") setPreview(response.data);
      else {
        const packet = response.data.packet;
        setMessage(packet.email_sent ? `Assignment email v${packet.version} sent to ${response.data.provider.email}.` : `Assignment v${packet.version} posted to ${response.data.provider.name}'s portal · email not delivered (${packet.email_error}).`);
        onSent?.(response.data);
        load();
      }
    } catch (e) { setError(formatApiError(e)); } finally { setBusy(false); }
  }

  if (error && !context) return <p className="form-message error-message" data-testid="packet-error">{error}</p>;
  if (!context) return <p className="projection-loading">Loading assignment…</p>;
  const { provider, gate, live, packet, client } = context;
  const canSend = provider && gate.status !== "blocked";
  return (
    <section className={`assignment-packet-panel ${compact ? "inline-edit-panel" : ""}`} data-testid="assignment-packet-panel">
      <div className="section-heading">
        <div>
          <h3><Mail size={16} /> Assignment email and portal page</h3>
          <p>{context.role_name} at {client.name} · to {provider ? `${provider.name}${provider.email ? ` (${provider.email})` : ""}` : "no provider portal account"}</p>
        </div>
        {onClose && <button aria-label="Close" className="projection-icon-button" data-testid="packet-close" type="button" onClick={onClose}><X size={16} /></button>}
      </div>
      {error && <p className="form-message error-message" data-testid="packet-error">{error}</p>}
      {message && <p className="form-message success-message" data-testid="packet-message">{message}</p>}
      {!client.profile_complete && client.id && <p className="form-message" data-testid="packet-profile-warning" style={{ background: "#FFF7E6", color: "#7A5A0D", border: "1px solid #F1DFAE" }}>{client.name} has no site profile yet — add the address, reporting location and site rules under Clients → Site profile so they appear in this email.</p>}
      {packet && (
        <p className={`packet-state ${packet.confirmed_at ? "is-confirmed" : "is-waiting"}`} data-testid="packet-state">
          {packet.confirmed_at ? <CheckCircle2 size={14} /> : <Mail size={14} />}
          v{packet.version} sent {new Date(packet.sent_at).toLocaleString()} by {packet.sent_by_name}{packet.changes?.length ? ` · updated: ${packet.changes.map((c) => c.label).join(", ")}` : ""} · {packet.confirmed_at ? `confirmed by provider ${new Date(packet.confirmed_at).toLocaleString()}` : "not yet confirmed"}
        </p>
      )}
      <GateCard assignmentId={assignmentId} gate={gate} isAdmin={isAdmin} onChanged={setContext} />
      {provider && (
        <div className="drawer-form-grid packet-form" data-testid="packet-form">
          <div className="form-section-label drawer-form-full">Pulled automatically</div>
          <dl className="packet-live drawer-form-full" data-testid="packet-live">
            <div><dt>Connecteam schedule</dt><dd data-testid="packet-live-schedule">{live.schedule.lines.length ? live.schedule.lines.join(" · ") : live.schedule.source === "unmapped" ? "Provider is not mapped to a Connecteam user yet" : "No published shifts in the next two weeks"}</dd></div>
            <div><dt>Caseload (initials)</dt><dd data-testid="packet-live-caseload">{live.caseload.length ? live.caseload.join(", ") : "No evaluations assigned at this client"}</dd></div>
            <div><dt>Supervisor</dt><dd data-testid="packet-live-supervisor">{live.supervisor?.name || "—"}</dd></div>
            <div><dt>Staffing coordinator</dt><dd>{context.coordinator?.name || "—"}</dd></div>
            <div><dt>Client documents</dt><dd data-testid="packet-live-documents">{context.documents.length ? context.documents.map((d) => `${d.title} v${d.version}`).join(", ") : "None in the library"}</dd></div>
            <div><dt>Template</dt><dd>
              <select data-testid="packet-template" value={form.template_id} onChange={(e) => setForm({ ...form, template_id: e.target.value })}>
                <option value="">Auto · {context.default_template.name}</option>
                {context.templates.map((t) => <option key={t.id} value={t.id}>{t.name}{t.client_name ? ` · ${t.client_name}` : ""}{t.role_name ? ` · ${t.role_name}` : ""}</option>)}
              </select>
            </dd></div>
          </dl>
          <div className="form-section-label drawer-form-full">Overrides and notes</div>
          <label className="drawer-form-full"><span className="field-label">Schedule override <em className="field-optional">· optional, one line per day</em></span><textarea data-testid="packet-schedule-override" placeholder="Leave blank to use the Connecteam schedule" rows={2} value={form.schedule_override} onChange={(e) => setForm({ ...form, schedule_override: e.target.value })} /></label>
          <label><span className="field-label">Caseload initials <em className="field-optional">· optional</em></span><input data-testid="packet-caseload" placeholder="e.g. J.R., M.T." value={form.caseload_initials} onChange={(e) => setForm({ ...form, caseload_initials: e.target.value })} /></label>
          <label><span className="field-label">Supervisor <em className="field-optional">· optional override</em></span><input data-testid="packet-supervisor" value={form.supervisor_name} onChange={(e) => setForm({ ...form, supervisor_name: e.target.value })} /></label>
          <label className="drawer-form-full"><span className="field-label">Note from the coordinator <em className="field-optional">· optional</em></span><textarea data-testid="packet-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></label>
          <div className="drawer-form-full form-actions">
            <button className="button button-secondary" data-testid="packet-preview" disabled={busy} type="button" onClick={() => run("preview")}><Eye size={14} /> Preview email</button>
            <button className="button button-primary" data-testid="packet-send" disabled={busy || !canSend} title={canSend ? "" : "Blocked by pre-start clearances"} type="button" onClick={() => run("send")}><Send size={14} /> {busy ? "Working…" : packet ? "Send updated assignment" : "Send assignment email"}</button>
          </div>
        </div>
      )}
      {provider && <IntroductionCard assignmentId={assignmentId} onSent={() => load()} />}
      {preview && <EmailPreview preview={preview} testId="packet-email-preview" onClose={() => setPreview(null)} />}
    </section>
  );
};
